import styled, { css } from 'styled-components';

import { purpurFocus } from '@telialt/common-theme/src/mixins';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

export const InputFixedHeight = styled.div`
    height: 3.75rem;
`;

type InputProps = {
    $isInputActive: boolean;
};

export const InputWrapper = styled.div<InputProps>`
    position: ${({ $isInputActive }) => ($isInputActive ? 'fixed' : 'relative')};
    display: flex;
    align-items: flex-start;
    justify-content: center;
    top: ${({ $isInputActive }) => ($isInputActive ? '0' : 'auto')};
    bottom: ${({ $isInputActive }) => ($isInputActive ? '0' : 'auto')};
    left: ${({ $isInputActive }) => ($isInputActive ? '0' : 'auto')};
    right: ${({ $isInputActive }) => ($isInputActive ? '0' : 'auto')};
    z-index: ${({ $isInputActive }) => ($isInputActive ? 1050 : 1048)};
    width: 100%;
    background: ${getPurpurColor('functionalWhite')};

    ${purpurMedia('lg')`
        position: relative;
        top: auto;
        left: auto;
        right: auto;
        z-index: 1048;
    `};
`;

export const InputContainer = styled.div<InputProps>`
    position: relative;
    max-width: ${({ $isInputActive }) => ($isInputActive ? '100%' : '50rem')};
    width: 100%;
    z-index: 1048;

    ${purpurMedia('lg')`
        max-width: 50rem;
        width: 100%;
    `};
`;

export const SearchButton = styled.button<InputProps>`
    background: ${getPurpurColor('functionalTransparent')};
    border: 0;
    padding: 0;
    position: absolute;
    height: 1.5rem;
    right: ${({ $isInputActive }) => ($isInputActive ? getPurpurSpacing('300') : 'auto')};
    left: ${({ $isInputActive }) => ($isInputActive ? 'auto' : getPurpurSpacing('300'))};
    top: 50%;
    transform: translateY(-50%);
    ${purpurFocus()};

    ${purpurMedia('lg')`
        left: ${getPurpurSpacing('300')};
        right: auto;
    `};
`;

export const ClearButton = styled.button<InputProps>`
    background: ${getPurpurColor('functionalTransparent')};
    border: 0;
    padding: 0;
    display: ${({ $isInputActive }) => ($isInputActive ? 'none' : 'block')};
    position: absolute;
    height: 1.5rem;
    right: ${getPurpurSpacing('300')};
    top: 50%;
    transform: translateY(-50%);
    ${purpurFocus()};

    ${purpurMedia('lg')`
        display: block;
    `};
`;

export const CloseButton = styled.button<InputProps>`
    background: ${getPurpurColor('functionalTransparent')};
    border: 0;
    padding: 0;
    display: ${({ $isInputActive }) => ($isInputActive ? 'block' : 'none')};
    position: absolute;
    height: 1.5rem;
    left: ${getPurpurSpacing('300')};
    top: 50%;
    transform: translateY(-50%);
    ${purpurFocus()};

    ${purpurMedia('lg')`
        display: none;
    `};
`;

const spinnerTopOffset = `calc(${getPurpurSpacing('150')} + ${getPurpurBorder('widthXs')})`;
export const SpinnerWrapper = styled.div<InputProps>`
    position: absolute;

    ${({ $isInputActive }) => css`
        ${purpurMedia('sm')`
            top: ${$isInputActive ? `calc(${getPurpurSpacing('200')} + ${getPurpurBorder('widthXs')})` : spinnerTopOffset};
            right: 3.4rem;
        `}
        ${purpurMedia('md')`
            top: ${$isInputActive ? `calc(${getPurpurSpacing('250')} + ${getPurpurBorder('widthXs')})` : spinnerTopOffset};
            right: ${getPurpurSpacing('800')};
        `}
        ${purpurMedia('lg')`
            top: ${spinnerTopOffset}
        `}
    `}
`;

export const StyledInput = styled.input<InputProps & { $isLoading: boolean }>`
    background-color: ${({ $isInputActive }) =>
        $isInputActive ? getPurpurColor('functionalWhite') : getPurpurColor('gray50')};
    border-radius: ${({ $isInputActive }) => ($isInputActive ? '0' : getPurpurBorder('radiusMd'))};
    border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray400')};
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray400')};
    border-left: ${({ $isInputActive }) =>
        $isInputActive ? '0' : `${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray400')}`};
    border-right: ${({ $isInputActive }) =>
        $isInputActive ? '0' : `${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray400')}`};
    display: flex;
    width: 100%;
    padding: ${({ $isInputActive }) =>
        $isInputActive ? `${getPurpurSpacing('250')} 3.5rem` : `${getPurpurSpacing('200')} ${getPurpurSpacing('800')}`};
    align-items: center;
    outline: none;
    font-size: ${getPurpurTypography('scale100')};
    line-height: ${getPurpurTypography('lineHeightLoose')};

    &:active,
    &:focus,
    &:hover {
        outline: ${getPurpurBorder('widthSm')} solid ${getPurpurColor('purple550')};
        outline-offset: -${getPurpurBorder('widthSm')};
        border-color: transparent;
    }

    ${({ $isInputActive }) => purpurMedia('md')`
        padding: ${
            $isInputActive
                ? `${getPurpurSpacing('300')} ${getPurpurSpacing('800')}`
                : `${getPurpurSpacing('200')} ${getPurpurSpacing('800')}`
        }
    `};

    ${purpurMedia('lg')`
        background-color: ${getPurpurColor('gray50')};
        border-radius: ${getPurpurBorder('radiusMd')};
        border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray400')};
        padding: ${getPurpurSpacing('200')} ${getPurpurSpacing('800')};

        &:hover {
            outline: ${getPurpurBorder('widthSm')} solid ${getPurpurColor('purple800')};
        }

        &:active,
        &:focus,
        &:hover {
            outline-offset: -${getPurpurBorder('widthXs')};
        }
    `};

    &:placeholder-shown ~ ${ClearButton} {
        display: none;
    }

    &:not(:placeholder-shown) {
        ${({ $isLoading }) =>
            $isLoading &&
            css`
                ${purpurMedia('sm')`
                        padding-right: 5.6rem;
                    `}
                ${purpurMedia('md')`
                        padding-right: 6.25rem;
                    `}
            `}
    }

    &:placeholder-shown ~ ${SpinnerWrapper} {
        ${({ $isInputActive }) =>
            !$isInputActive &&
            purpurMedia('sm')`
                    right: ${getPurpurSpacing('300')};
                `}
        ${purpurMedia('lg')`
            right: ${getPurpurSpacing('300')};
        `}
    }
`;
